import { Type, Static } from '@sinclair/typebox'

import { ConsumablesRecordTB, ConsumablesFormTB, NumberMinZeroDefaultUndefinedTB } from './report_shared';
import { PropertyT, PropertyWithReportSummariesT } from './property';

export const ProcessorConsumablesRecordTB = Type.Composite([
  ConsumablesRecordTB,
  Type.Object({
    fuelOil: NumberMinZeroDefaultUndefinedTB(),
    coal: NumberMinZeroDefaultUndefinedTB(),
    lpg: NumberMinZeroDefaultUndefinedTB(),
    naturalGas: NumberMinZeroDefaultUndefinedTB(),
  })
])
export type ProcessorConsumablesRecordT = Static<typeof ProcessorConsumablesRecordTB>;

export const ProcessorConsumablesFormTB = Type.Composite([
  ConsumablesFormTB,
  Type.Object({
    fuelOil: NumberMinZeroDefaultUndefinedTB(),
    coal: NumberMinZeroDefaultUndefinedTB(),
    lpg: NumberMinZeroDefaultUndefinedTB(),
    naturalGas: NumberMinZeroDefaultUndefinedTB(),
  })
])
export type ProcessorConsumablesFormT = Static<typeof ProcessorConsumablesFormTB>;

// transform functions from db -> client form and client form -> db
export function transformProcessorConsumablesRecordToForm(record: ProcessorConsumablesRecordT): ProcessorConsumablesFormT {
  return {
    ...record,
    percentRenewables: record.percentRenewables * 100, // decimal -> percentage
  };
}
export function transformProcessorConsumablesFormToRecord(form: ProcessorConsumablesFormT): ProcessorConsumablesRecordT {
  return {
    ...form,
    percentRenewables: form.percentRenewables / 100, // percentage -> decimal
  };
}

export const ProcessorMethaneRecordTB = Type.Object({
  methaneEmissions: NumberMinZeroDefaultUndefinedTB(),
  percentageFlared: NumberMinZeroDefaultUndefinedTB({ maximum: 1 }),
  percentageCaptured: NumberMinZeroDefaultUndefinedTB({ maximum: 1 }),
})
export type ProcessorMethaneRecordT = Static<typeof ProcessorMethaneRecordTB>;

export const ProcessorMethaneFormTB = Type.Object({
  methaneEmissions: NumberMinZeroDefaultUndefinedTB(),
  percentageFlared: NumberMinZeroDefaultUndefinedTB({ maximum: 100 }),
  percentageCaptured: NumberMinZeroDefaultUndefinedTB({ maximum: 100 }),
})
export type ProcessorMethaneFormT = Static<typeof ProcessorMethaneFormTB>;

// transform functions from db -> client form and client form -> db
export function transformProcessorMethaneRecordToForm(record: ProcessorMethaneRecordT): ProcessorMethaneFormT {
  return {
    ...record,
    percentageFlared: record.percentageFlared * 100, // decimal -> percentage
    percentageCaptured: record.percentageCaptured * 100, // decimal -> percentage
  };
}
export function transformProcessorMethaneFormToRecord(form: ProcessorMethaneFormT): ProcessorMethaneRecordT {
  return {
    ...form,
    percentageFlared: form.percentageFlared / 100, // percentage -> decimal
    percentageCaptured: form.percentageCaptured / 100, // percentage -> decimal
  };
}

export const ProcessorRefrigerantsTB = Type.Object({
  ammonia: NumberMinZeroDefaultUndefinedTB(),
  carbonDioxide: NumberMinZeroDefaultUndefinedTB(),
  liquidNitrogen: NumberMinZeroDefaultUndefinedTB(),
  r22: NumberMinZeroDefaultUndefinedTB(),
  r407C: NumberMinZeroDefaultUndefinedTB(),
})
export type ProcessorRefrigerantsT = Static<typeof ProcessorRefrigerantsTB>;

export const ProcessorManureManagementTypeManagedAerobicTreatmentTB = Type.Literal("managed-aerobic-treatment");
export const ProcessorManureManagementTypeUnmanagedAerobicTreatmentTB = Type.Literal("unmanaged-aerobic-treatment");
export const ProcessorManureManagementTypeAnaerobicDigesterTB = Type.Literal("anaerobic-digester");
export const ProcessorManureManagementTypeAnaerobicShallowLagoonTB = Type.Literal("anaerobic-shallow-lagoon");
export const ProcessorManureManagementTypeAnaerobicDeepLagoonTB = Type.Literal("anaerobic-deep-lagoon");

export const ProcessorManureManagementTypeTB = Type.Union([
  ProcessorManureManagementTypeManagedAerobicTreatmentTB,
  ProcessorManureManagementTypeUnmanagedAerobicTreatmentTB,
  ProcessorManureManagementTypeAnaerobicDigesterTB,
  ProcessorManureManagementTypeAnaerobicShallowLagoonTB,
  ProcessorManureManagementTypeAnaerobicDeepLagoonTB
])
export type ProcessorManureManagementTypeT = Static<typeof ProcessorManureManagementTypeTB>

export const ProcessorManureManagementTypeMapTB = Type.Object({
  'Managed Aerobic Treatment': ProcessorManureManagementTypeManagedAerobicTreatmentTB,
  'Unmanaged Aerobic Treatment': ProcessorManureManagementTypeUnmanagedAerobicTreatmentTB,
  'Anaerobic Digester': ProcessorManureManagementTypeAnaerobicDigesterTB,
  'Anaerobic Shallow Lagoon': ProcessorManureManagementTypeAnaerobicShallowLagoonTB,
  'Anaerobic Deep Lagoon': ProcessorManureManagementTypeAnaerobicDeepLagoonTB
})
export type ProcessorManureManagementTypeMapTB = Static<typeof ProcessorManureManagementTypeMapTB>

export const ProportionEffluentTB = Type.Object({
  land: NumberMinZeroDefaultUndefinedTB({ maximum: 100 }),
  recycling: NumberMinZeroDefaultUndefinedTB({ maximum: 100 }),
  evaporation: NumberMinZeroDefaultUndefinedTB({ maximum: 100 }),
  sewer: NumberMinZeroDefaultUndefinedTB({ maximum: 100 }),
  pondOrDigester: NumberMinZeroDefaultUndefinedTB({ maximum: 100 }),
  sludge: NumberMinZeroDefaultUndefinedTB({ maximum: 100 })
})
export type ProportionEffluentT = Static<typeof ProportionEffluentTB>

export const ProcessorEffluentManagementRecordTB = Type.Object({
  manureManagementSystem: ProcessorManureManagementTypeTB,
  volumeEffluentLiters: NumberMinZeroDefaultUndefinedTB(),
  codOfEffluent: NumberMinZeroDefaultUndefinedTB(),
  nitrogenOfEffluent: NumberMinZeroDefaultUndefinedTB(),
  bioGasCaptured: Type.Boolean(),
  proportionEffluent: Type.Object({
    land: Type.Number({ maximum: 1 }),
    recycling: Type.Number({ maximum: 1 }),
    evaporation: Type.Number({ maximum: 1 }),
    sewer: Type.Number({ maximum: 1 }),
    pondOrDigester: Type.Number({ maximum: 1 }),
    sludge: Type.Number({ maximum: 1 })
  })
})
export type ProcessorEffluentManagementRecordT = Static<typeof ProcessorEffluentManagementRecordTB>;

export const ProcessorEffluentManagementFormTB = Type.Object({
  manureManagementSystem: ProcessorManureManagementTypeTB,
  volumeEffluentLiters: NumberMinZeroDefaultUndefinedTB(),
  codOfEffluent: NumberMinZeroDefaultUndefinedTB(),
  nitrogenOfEffluent: NumberMinZeroDefaultUndefinedTB(),
  bioGasCaptured: Type.Boolean(),
  proportionEffluent: ProportionEffluentTB
})
export type ProcessorEffluentManagementFormT = Static<typeof ProcessorEffluentManagementFormTB>;

// transform functions from db -> client form and client form -> db
export function transformProcessorEffluentManagementRecordToForm(record: ProcessorEffluentManagementRecordT): ProcessorEffluentManagementFormT {
  return {
    ...record,
    proportionEffluent: {
      land: record.proportionEffluent.land * 100,
      recycling: record.proportionEffluent.recycling * 100,
      evaporation: record.proportionEffluent.evaporation * 100,
      sewer: record.proportionEffluent.sewer * 100,
      pondOrDigester: record.proportionEffluent.pondOrDigester * 100,
      sludge: record.proportionEffluent.sludge * 100
    }
  };
}
export function transformProcessorEffluentManagementFormToRecord(form: ProcessorEffluentManagementFormT): ProcessorEffluentManagementRecordT {
  return {
    ...form,
    proportionEffluent: {
      land: form.proportionEffluent.land / 100,
      recycling: form.proportionEffluent.recycling / 100,
      evaporation: form.proportionEffluent.evaporation / 100,
      sewer: form.proportionEffluent.sewer / 100,
      pondOrDigester: form.proportionEffluent.pondOrDigester / 100,
      sludge: form.proportionEffluent.sludge / 100
    }
  };
}

export const ProcessorWaterUseTB = Type.Object({
  townWater: NumberMinZeroDefaultUndefinedTB(),
  boreWater: NumberMinZeroDefaultUndefinedTB(),
  riverWater: NumberMinZeroDefaultUndefinedTB(),
  recycledWater: NumberMinZeroDefaultUndefinedTB(),
  wasteWater: NumberMinZeroDefaultUndefinedTB(),
  runoffWater: NumberMinZeroDefaultUndefinedTB(),
})
export type ProcessorWaterUseT = Static<typeof ProcessorWaterUseTB>;

// TODO: may need to be broken up as both beef and sheep aren't always required, depending on processor type
export const ProcessorProductionTB = Type.Object({
  totalBeefMeatSold: NumberMinZeroDefaultUndefinedTB(),
  totalBeefByproductsSold: NumberMinZeroDefaultUndefinedTB(),
  totalSheepMeatSold: NumberMinZeroDefaultUndefinedTB(),
  totalSheepByproductsSold: NumberMinZeroDefaultUndefinedTB(),
})
export type ProcessorProductionT = Static<typeof ProcessorProductionTB>;

export function getProcessorProductionSchema (property: PropertyT | PropertyWithReportSummariesT | undefined) {
  return Type.Object({
    ...(property?.sheepProcessor ? {
      totalSheepMeatSold: NumberMinZeroDefaultUndefinedTB(),
      totalSheepByproductsSold: NumberMinZeroDefaultUndefinedTB()
    } : undefined),
    ...(property?.beefProcessor ? {
      totalBeefMeatSold: NumberMinZeroDefaultUndefinedTB(),
      totalBeefByproductsSold: NumberMinZeroDefaultUndefinedTB()
    } : undefined)
  })
}

export const ConsumablesLPGTB = Type.Object({
  type: Type.Union([Type.Literal('kgs'), Type.Literal('litres')]),
  amount: NumberMinZeroDefaultUndefinedTB(),
})
export type ConsumablesLPGT = Static<typeof ConsumablesLPGTB>;
